import React from 'react';
import { STATUS } from "../../shellTokens";
import { isLowStock, LOW_STOCK_KG } from "../Stock/fabricShelves";
import { Mono } from "./orderFormShared";

const fmtKg = (n) => Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

/**
 * What the fabric store holds for the cloth one order line names.
 *
 * An aid, not a rule: an order may well be placed for cloth that is not in yet,
 * so an empty shelf says so in red and then stays out of the way. Nothing here
 * blocks a submit, and nothing here is authoritative — the store moves while a
 * form is open.
 *
 * Silent when there is no answer to give: before a line names both a fabric and
 * a colour there is no shelf to report on, and where the balances never arrived
 * — a viewer the fabric store is not open to — the form reads as it always did.
 *
 * Lifted out of OrderProductForm so it can be rendered on its own in a test:
 * that file reaches the network through useFabricPatterns, and what is worth
 * checking here is only what the figures are turned into.
 *
 * @param held  an onHandForLine() answer, or null
 */
const StockNote = ({ held, identity }) => {
    if (!held) return null;

    const empty = held.weight <= 0;
    const low = !empty && isLowStock(held.weight);
    const color = empty ? STATUS.errorSoft : low ? STATUS.warn : "var(--dim)";
    // With no print picked the figure is the cloth across every print it is
    // carried in, and no one line can draw on all of them — so say which it is
    // rather than letting a total read as available to this line.
    const spread = !held.exact && held.prints > 1 ? ` across ${held.prints} prints` : "";

    return (
        <Mono
            style={{ flex: "none", fontSize: 11.5, fontWeight: 600, color }}
            title={[
                identity,
                empty
                    ? "nothing on hand in the fabric store"
                    : `${fmtKg(held.weight)} kg · ${held.rolls} roll${held.rolls === 1 ? "" : "s"} on hand`,
                low ? `below ${LOW_STOCK_KG} kg — running low` : null,
            ].filter(Boolean).join(" — ")}
        >
            {empty ? "not in stock" : `${fmtKg(held.weight)} kg on hand${spread}`}
        </Mono>
    );
};

export default StockNote;
